import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClientSelectSearchResponse, ClientSelectService } from '@app/core/services/client-select.service';
import { Constants } from '@app/core/services/constants';
import { ContextService } from '@app/core/services/context.service';
import { MiscService } from '@app/core/services/misc.service';
import { Setting, SettingsService, SettingType } from '@app/core/services/settings.service';
import { FilterCriteria } from '@app/shared/components/filter-sidebar/filter-sidebar.component';
import { FormlyModule } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import Mustache from 'mustache';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { FormlyFieldAnyComponent } from '../formly-field-any/formly-field-any.component';

type ClientSelectValueMode = 'ids' | 'objects';

interface ClientSelectColumnDefinition {
  label: string;
  value: string;
}

interface ClientSelectFieldProps {
  valueMode: ClientSelectValueMode;
  locationReferenceFieldKey?: string;
  sourceKey: string;
  apiRoute: string;
  filters?: FilterCriteria[];
  columns: Array<string | ClientSelectColumnDefinition>;
  modelColumns?: string[];
  idField: string;
  secondarySortField: string;
  multiple?: boolean;
  minSearchLength?: number;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
}

interface ClientSelectLocationValue {
  latitude?: number | null;
  longitude?: number | null;
}

interface ClientSelectDisplayRow {
  id: string;
  record: any;
  cells: string[];
}

@Component({
  selector: 'app-formly-field-client-select',
  templateUrl: './formly-field-client-select.component.html',
  styleUrls: ['./formly-field-client-select.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ReactiveFormsModule,
    FormlyModule,
  ],
})
export class FormlyFieldClientSelectComponent extends FormlyFieldAnyComponent implements OnInit, OnDestroy {
  public readonly pageSize = Constants.pageSize;
  public readonly searchDebounceMs = 300;
  public readonly loadErrorKey = 'CLIENT_SELECT.ERROR.LOAD';
  public readonly loadMoreErrorKey = 'CLIENT_SELECT.ERROR.LOAD_MORE';

  public selectedRecords: any[] = [];
  public modalResults: any[] = [];
  public selectedDisplayRows: ClientSelectDisplayRow[] = [];
  public visibleModalDisplayRows: ClientSelectDisplayRow[] = [];
  public searchText = '';
  public isPickerOpen = false;
  public isContextReady = false;
  public isHydrating = false;
  public isLoading = false;
  public isLoadingMore = false;
  public loadError: string | null = null;
  public totalResults = 0;
  public currentPage = 1;
  public showMissingItemsWarning = false;

  private readonly subsink = new SubSink();
  private readonly searchInput$ = new Subject<string>();
  private clientId: string | null = null;
  private subsidiaryId: string | null = null;
  private suppressValueSync = false;
  private hasBoundFormControl = false;
  private mustacheHelpers: Record<string, unknown> = {};
  private helpersContextKey: string | null = null;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly contextService: ContextService,
    private readonly clientSelectService: ClientSelectService,
    private readonly settingsService: SettingsService,
    private readonly miscService: MiscService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subsink.sink = this.contextService.getContext$().subscribe((context) => {
      this.clientId = context.clientId;
      this.subsidiaryId = context.subsidiaryId;
      this.isContextReady = Boolean(context.clientId && context.subsidiaryId);
      void this.loadMustacheHelpers();
      this.bindFormControlWhenReady();
      void this.syncSelectedRecordsFromValue(this.getCurrentStoredValue());
      this.changeDetectorRef.markForCheck();
    });

    this.subsink.sink = this.searchInput$
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        if (!this.isPickerOpen) {
          return;
        }
        void this.loadFirstPage();
      });
    this.bindFormControlWhenReady();
    void this.syncSelectedRecordsFromValue(this.getCurrentStoredValue());
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  public get fieldProps(): ClientSelectFieldProps {
    return this.field.props as ClientSelectFieldProps;
  }

  public get isMultiple(): boolean {
    return Boolean(this.fieldProps.multiple);
  }

  public get minSearchLength(): number {
    return this.fieldProps.minSearchLength ?? 4;
  }

  public get configuredColumns(): ClientSelectColumnDefinition[] {
    return this.fieldProps.columns.map((column) => this.normalizeColumnDefinition(column));
  }

  public get selectedColumns(): ClientSelectColumnDefinition[] {
    return this.configuredColumns.filter((column) => !this.isDistanceColumn(column));
  }

  public get modelColumns(): string[] {
    return this.fieldProps.modelColumns?.length
      ? this.fieldProps.modelColumns
      : this.configuredColumns
          .map((column) => column.value)
          .filter((value) => value !== 'distance' && !value.includes('{{'));
  }

  public get isInteractionDisabled(): boolean {
    return Boolean(this.options.formState?.disabled || this.fieldProps.disabled || this.fieldProps.readonly || !this.isContextReady);
  }

  public get canAddItem(): boolean {
    if (this.isInteractionDisabled) {
      return false;
    }

    if (this.isMultiple) {
      return true;
    }

    return this.selectedRecords.length === 0;
  }

  /**
   * Returns the placeholder text for the input field. It prioritizes the field's placeholder property, then the field's label, and defaults to 'Select an item' if neither is provided.
   * @returns The placeholder text.
   */
  public get placeholderText(): string {
    return this.fieldProps.placeholder || this.field.props['label'] || 'Select an item';
  }

  /**
   * Determines the columns to be displayed in the modal based on the field configuration and the reference location. If a reference location is available and the 'distance' column is not already included in the configured columns, it prepends the 'distance' column to the list of columns. This allows the modal to display the distance from the reference location to each item when applicable, providing additional context to the user when making a selection.
   * @returns An array of column names to be displayed in the modal.
   */
  public get modalColumns(): ClientSelectColumnDefinition[] {
    const columns = [...this.configuredColumns];
    if (this.shouldShowDistanceColumn(columns)) {
      return [{ label: 'Distance', value: 'distance' }, ...columns];
    }
    return columns;
  }

  /**
   * Returns the CSS grid template columns for the modal based on the number of columns to be displayed.
   * @returns A string representing the CSS grid template columns.
   */
  public get modalGridTemplateColumns(): string {
    return `repeat(${Math.max(this.modalColumns.length, 1)}, fit-content(14rem))`;
  }

  public get selectedGridTemplateColumns(): string {
    const actionColumn = this.isInteractionDisabled ? '' : ' fit-content(2.5rem)';
    return `repeat(${Math.max(this.selectedColumns.length, 1)}, fit-content(14rem))${actionColumn}`;
  }

  public get hasMoreResults(): boolean {
    return this.modalResults.length < this.totalResults;
  }

  public openPicker(): void {
    if (this.isInteractionDisabled) {
      return;
    }

    this.isPickerOpen = true;
    void this.loadFirstPage();
  }

  public closePicker(): void {
    this.isPickerOpen = false;
    this.changeDetectorRef.markForCheck();
  }

  public onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? this.searchText;
    this.searchText = value;
    this.searchInput$.next(value);
  }

  public async retryLoad(): Promise<void> {
    await this.loadFirstPage();
  }

  public onResultsScroll(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const threshold = 80;
    const remainingScroll = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remainingScroll <= threshold) {
      void this.loadNextPage();
    }
  }

  /**
   * Selects a record and updates the selected records list. This method handles both single and multiple selection modes. In single selection mode, it replaces the current selection with the new record. In multiple selection mode, it adds the new record to the existing selection. It also resets the missing items warning flag and writes the updated selection to the form control.
   * @param record The record to select.
   */
  public async selectRecord(record: any): Promise<void> {
    this.selectedRecords = this.isMultiple ? [...this.selectedRecords, record] : [record];
    this.showMissingItemsWarning = false;
    this.refreshSelectedDisplayRows();
    this.refreshVisibleModalDisplayRows();
    this.writeSelectionToForm();
    this.closePicker();
  }

  /**
   * Removes a selected record at the specified index. This method updates the selected records by filtering out the record at the given index, resets the missing items warning flag, and then writes the updated selection back to the form control to ensure that the form state is consistent with the current selection. This allows users to easily remove items from their selection and have those changes reflected in the form.
   * @param index The index of the record to remove.
   */
  public removeSelectedRecord(index: number): void {
    this.selectedRecords = this.selectedRecords.filter((_, recordIndex) => recordIndex !== index);
    this.showMissingItemsWarning = false;
    this.refreshSelectedDisplayRows();
    this.refreshVisibleModalDisplayRows();
    this.writeSelectionToForm();
  }

  /**
   * Retrieves the label for a given column path. This method handles special cases such as the 'distance' column and provides a default label if none is specified in the field properties.
   * @param columnPath The path to the column.
   * @returns The label for the column.
   */
  public getColumnLabel(column: ClientSelectColumnDefinition): string {
    return column.label;
  }

  /**
   * Retrieves the display value for a given record and column path. This method handles special cases such as the 'distance' column and formats the value accordingly. It also provides default handling for null, undefined, empty strings, arrays, and objects. For the 'distance' column, it formats the distance value using the miscService. For other columns, it retrieves the nested value from the record based on the column path and formats it based on its type (e.g., arrays are joined with commas, objects are stringified). If the value is null, undefined, or an empty string, it returns a placeholder (e.g., '—') to indicate that there is no value.
   * @param record The record from which to retrieve the value.
   * @param columnPath The path to the column value within the record.
   * @returns The formatted display value.
   */
  public getDisplayValue(record: any, column: ClientSelectColumnDefinition): string {
    if (this.isDistanceColumn(column)) {
      return this.formatDistance(record?.distance);
    }

    if (column.value.includes('{{')) {
      const renderedValue = Mustache.render(this.normalizeMustacheTemplate(column.value), this.buildTemplateContext(record)).trim();
      return renderedValue || '—';
    }

    return this.formatPlainColumnValue(this.getNestedValue(record, column.value));
  }

  /**
   * Synchronizes the selected records from the given raw value. The raw value can be in the form of IDs or full objects depending on the field configuration. If the raw value contains IDs, it will attempt to hydrate them into full records using the client select service. If hydration fails, it will fall back to normalizing the raw value into objects without hydration.
   * @param rawValue The raw value from which to synchronize the selected records. This can be an ID, an array of IDs, an object, or an array of objects depending on the field's value mode and multiplicity.
   * @returns A promise that resolves when the synchronization is complete.
   */
  private async syncSelectedRecordsFromValue(rawValue: any): Promise<void> {
    const ids = this.extractIds(rawValue);
    if (!ids.length || !this.isContextReady || !this.fieldProps.sourceKey) {
      this.selectedRecords = this.normalizeStoredObjects(rawValue);
      this.showMissingItemsWarning = false;
      this.refreshSelectedDisplayRows();
      this.refreshVisibleModalDisplayRows();
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.isHydrating = true;
    this.changeDetectorRef.markForCheck();

    try {
      const location = this.getReferenceLocation();
      const response = await firstValueFrom(
        this.clientSelectService.hydrateRecords<any>({
          routeTemplate: this.fieldProps.apiRoute,
          clientId: this.clientId!,
          subsidiaryId: this.subsidiaryId!,
          sourceKey: this.fieldProps.sourceKey,
          filters: this.fieldProps.filters,
          idField: this.fieldProps.idField,
          secondarySortField: this.fieldProps.secondarySortField,
          ids,
          lat: location?.latitude ?? null,
          lng: location?.longitude ?? null,
        }),
      );

      this.selectedRecords = this.mergeHydratedRecordsWithFallback(rawValue, response.items);
      this.showMissingItemsWarning = response.missingIds.length > 0;
    } catch (error) {
      console.error('Failed to hydrate client-select records', error);
      this.selectedRecords = this.mergeHydratedRecordsWithFallback(rawValue, []);
      this.showMissingItemsWarning = false;
    } finally {
      this.refreshSelectedDisplayRows();
      this.refreshVisibleModalDisplayRows();
      this.isHydrating = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Loads the first page of client-select results. This method resets the current page to 1 and fetches the initial set of results. It also handles the loading state and any errors that may occur during the fetch. This method should be called when the picker is opened or when the search text changes to refresh the results based on the new search criteria.
   * @returns A promise that resolves when the first page is loaded.
   */
  private async loadFirstPage(): Promise<void> {
    if (!this.isContextReady) {
      return;
    }

    this.isLoading = true;
    this.isLoadingMore = false;
    this.currentPage = 1;
    this.loadError = null;
    this.changeDetectorRef.markForCheck();

    try {
      const response = await this.fetchPage(1);
      this.modalResults = response.items;
      this.totalResults = response.total;
      this.refreshVisibleModalDisplayRows();
    } catch (error) {
      console.error('Failed to load client-select page', error);
      this.modalResults = [];
      this.totalResults = 0;
      this.loadError = this.loadErrorKey;
      this.refreshVisibleModalDisplayRows();
    } finally {
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Loads the next page of client-select results. This method increments the current page and fetches the next set of results. It also handles the loading state and any errors that may occur during the fetch. This method should be called when the user scrolls to the bottom of the results or when more results are needed.
   * @returns A promise that resolves when the next page is loaded.
   */
  private async loadNextPage(): Promise<void> {
    if (!this.hasMoreResults || this.isLoading || this.isLoadingMore || !this.isContextReady) {
      return;
    }

    this.isLoadingMore = true;
    this.changeDetectorRef.markForCheck();

    try {
      const nextPage = this.currentPage + 1;
      const response = await this.fetchPage(nextPage);
      this.currentPage = nextPage;
      this.modalResults = [...this.modalResults, ...response.items];
      this.totalResults = response.total;
      this.refreshVisibleModalDisplayRows();
    } catch (error) {
      console.error('Failed to load the next client-select page', error);
      this.loadError = this.loadMoreErrorKey;
    } finally {
      this.isLoadingMore = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Fetches a page of client-select results. This method sends a request to the client-select service with the specified page number and other search parameters. It returns the response containing the items for that page along with pagination information. This method is used internally by the loadFirstPage and loadNextPage methods to retrieve the appropriate set of results based on the current search criteria and pagination state.
   * @param page The page number to fetch.
   * @returns A promise that resolves with the client-select search response for the specified page.
   */
  private async fetchPage(page: number): Promise<ClientSelectSearchResponse<any>> {
    const location = this.getReferenceLocation();
    return firstValueFrom(
      this.clientSelectService.searchRecords<any>({
        routeTemplate: this.fieldProps.apiRoute,
        clientId: this.clientId!,
        subsidiaryId: this.subsidiaryId!,
        sourceKey: this.fieldProps.sourceKey,
        filters: this.fieldProps.filters,
        idField: this.fieldProps.idField,
        secondarySortField: this.fieldProps.secondarySortField,
        page,
        pageSize: this.pageSize,
        search: this.getEffectiveSearchText(),
        lat: location?.latitude ?? null,
        lng: location?.longitude ?? null,
      }),
    );
  }

  /**
   * Writes the selected value(s) to the form control and updates the model. This method handles both single and multiple selections, ensuring that the form control is updated correctly and that the change detection is triggered. It also manages a flag to suppress value synchronization during the update process to prevent unintended side effects. After updating the form control and model, it triggers the onChange callback to notify Formly of the change and marks the component for check to update the view accordingly.
   */
  private writeSelectionToForm(): void {
    const value = this.buildStoredValue();
    this.bindFormControlWhenReady();
    const control = this.getResolvedFormControl();

    this.writeSelectionToModel(value);

    if (control) {
      this.suppressValueSync = true;
      control.setValue(value);
      control.markAsDirty();
      control.markAsTouched();
    }

    this.onChange(value);
    queueMicrotask(() => {
      this.suppressValueSync = false;
      this.changeDetectorRef.markForCheck();
    });
  }

  /**
   * Builds the value to be stored in the form control based on the selected records and the value mode. If the value mode is 'ids', it extracts the IDs from the selected records. If the value mode is 'objects', it constructs objects containing the necessary fields based on the selected records and the field configuration. This method ensures that the value stored in the form control is consistent with the expected format based on the field's value mode and multiplicity.
   * @returns The value to be stored in the form control.
   */
  private buildStoredValue(): any {
    if (this.isMultiple) {
      return this.selectedRecords.map((record) => this.buildStoredItem(record));
    }

    const [record] = this.selectedRecords;
    return record ? this.buildStoredItem(record) : null;
  }

  /**
   * Builds the value to be stored in the form control for a single record based on the value mode.
   * @param record The record to build the stored value from.
   * @returns The value to be stored in the form control for the given record.
   */
  private buildStoredItem(record: any): any {
    if (this.fieldProps.valueMode === 'ids') {
      return this.getNestedValue(record, this.fieldProps.idField);
    }

    const reducedRecord: Record<string, unknown> = {};
    const requiredPaths = Array.from(new Set([this.fieldProps.idField, ...this.modelColumns]));
    for (const path of requiredPaths) {
      const value = this.getNestedValue(record, path);
      if (value === undefined) {
        continue;
      }
      this.setNestedValue(reducedRecord, path, value);
    }

    return reducedRecord;
  }

  /**
   * Extracts the IDs from the given raw value based on the value mode and multiplicity.
   * @param rawValue The raw value to extract IDs from.
   * @returns An array of extracted IDs.
   */
  private extractIds(rawValue: any): Array<string | number> {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return [];
    }

    if (this.fieldProps.valueMode === 'ids') {
      if (this.isMultiple) {
        return Array.isArray(rawValue) ? rawValue.filter((value) => value !== null && value !== undefined && value !== '') : [];
      }
      return [rawValue].filter((value) => value !== null && value !== undefined && value !== '');
    }

    const values = this.isMultiple ? (Array.isArray(rawValue) ? rawValue : []) : [rawValue];
    return values
      .map((value) => this.getNestedValue(value, this.fieldProps.idField))
      .filter((value) => value !== null && value !== undefined && value !== '');
  }

  /**
   * Normalizes the given raw value into an array of stored objects based on the value mode and multiplicity.
   * @param rawValue The raw value to normalize.
   * @returns An array of normalized stored objects.
   */
  private normalizeStoredObjects(rawValue: any): any[] {
    if (this.fieldProps.valueMode !== 'objects') {
      return [];
    }

    if (this.isMultiple) {
      return Array.isArray(rawValue) ? rawValue : [];
    }

    return rawValue ? [rawValue] : [];
  }

  /**
   * Combines hydrated backend records with locally stored values for IDs that could not be hydrated.
   * Preserves the original selection order.
   */
  private mergeHydratedRecordsWithFallback(rawValue: any, hydratedItems: any[]): any[] {
    const requestedIds = this.extractIds(rawValue);
    if (!requestedIds.length) {
      return hydratedItems;
    }

    const hydratedById = new Map(
      hydratedItems.map((record) => [String(this.getNestedValue(record, this.fieldProps.idField)), record]),
    );
    const fallbackById = new Map(
      this.normalizeStoredObjects(rawValue).map((record) => [
        String(this.getNestedValue(record, this.fieldProps.idField)),
        record,
      ]),
    );

    return requestedIds.map((id) => {
      const idKey = String(id);
      return hydratedById.get(idKey) ?? fallbackById.get(idKey) ?? this.buildFallbackRecord(id);
    });
  }

  private buildFallbackRecord(id: string | number): any {
    const record: Record<string, unknown> = {};
    this.setNestedValue(record, this.fieldProps.idField, id);
    return record;
  }

  private refreshSelectedDisplayRows(): void {
    this.selectedDisplayRows = this.selectedRecords.map((record, index) => this.buildDisplayRow(record, this.selectedColumns, `${index}`));
  }

  private refreshVisibleModalDisplayRows(): void {
    const selectedIds = this.isMultiple
      ? new Set(this.selectedRecords.map((record) => String(this.getNestedValue(record, this.fieldProps.idField))))
      : null;

    this.visibleModalDisplayRows = this.modalResults
      .filter((record) => !selectedIds || !selectedIds.has(String(this.getNestedValue(record, this.fieldProps.idField))))
      .map((record, index) => this.buildDisplayRow(record, this.modalColumns, `${this.currentPage}-${index}`));
  }

  private buildDisplayRow(record: any, columns: ClientSelectColumnDefinition[], fallbackId: string): ClientSelectDisplayRow {
    const rawId = this.getNestedValue(record, this.fieldProps.idField);
    return {
      id: rawId === null || rawId === undefined || rawId === '' ? fallbackId : String(rawId),
      record,
      cells: columns.map((column) => this.getDisplayValue(record, column)),
    };
  }

  /**
   * Retrieves the effective search text based on the minimum search length.
   * @returns The effective search text or undefined if it doesn't meet the minimum length.
   */
  private getEffectiveSearchText(): string | undefined {
    const trimmedSearch = this.searchText.trim();
    return trimmedSearch.length >= this.minSearchLength ? trimmedSearch : undefined;
  }

  /**
   * Determines whether the distance column should be shown based on the reference location and existing columns.
   * @param columns The array of column names.
   * @returns True if the distance column should be shown, false otherwise.
   */
  private shouldShowDistanceColumn(columns: ClientSelectColumnDefinition[]): boolean {
    return Boolean(this.getReferenceLocation() && !columns.some((column) => this.isDistanceColumn(column)));
  }

  private normalizeColumnDefinition(column: string | ClientSelectColumnDefinition): ClientSelectColumnDefinition {
    if (typeof column === 'string') {
      return {
        label: column === 'distance' ? 'Distance' : column,
        value: column,
      };
    }

    return column;
  }

  private isDistanceColumn(column: ClientSelectColumnDefinition): boolean {
    return column.value === 'distance';
  }

  private normalizeMustacheTemplate(template: string): string {
    return template
      .replace(/\{\{#unless\s+@last\s*\}\}/g, '{{^@last}}')
      .replace(/\{\{\/unless\s*\}\}/g, '{{/@last}}');
  }

  private buildTemplateContext(record: any): any {
    const templateRecord = this.decorateTemplateValue({
      ...record,
      distance: this.formatDistance(record?.distance),
    });

    return {
      ...templateRecord,
      ...this.mustacheHelpers,
      helpers: this.mustacheHelpers,
    };
  }

  private async loadMustacheHelpers(): Promise<void> {
    const contextKey = this.clientId && this.subsidiaryId ? `${this.clientId}:${this.subsidiaryId}` : null;
    if (!contextKey) {
      this.helpersContextKey = null;
      this.mustacheHelpers = {};
      return;
    }

    if (this.helpersContextKey === contextKey) {
      return;
    }

    this.helpersContextKey = contextKey;

    try {
      const settings = await firstValueFrom(
        this.settingsService.getSettingsByType(SettingType.HELPERS, this.clientId!, this.subsidiaryId!),
      );
      const helperSetting = settings.find((setting) => setting.entityType === 'helpers.js');
      const helpersCode = typeof helperSetting?.value?.code === 'string' ? helperSetting.value.code : '';
      this.mustacheHelpers = this.compileMustacheHelpers(helpersCode);
    } catch (error) {
      console.error('Failed to load client-select mustache helpers', error);
      this.mustacheHelpers = {};
    }

    this.refreshSelectedDisplayRows();
    this.refreshVisibleModalDisplayRows();
    this.changeDetectorRef.markForCheck();
  }

  private compileMustacheHelpers(code: string): Record<string, unknown> {
    if (!code.trim()) {
      return {};
    }

    const helpers: Record<string, unknown> = {};
    const module = { exports: helpers };
    const exports = module.exports;
    const registeredHelpers: Record<string, unknown> = {};
    const Handlebars = {
      registerHelper: (name: string, helper: unknown) => {
        registeredHelpers[name] = this.convertRegisteredHelperToMustache(name, helper);
      },
    };
    const require = (modulePath: string): unknown => {
      if (modulePath.includes('media.service')) {
        return {
          getMedia: async () => ({ status: 404, dataUrl: '' }),
        };
      }

      return {};
    };

    const evaluator = new Function(
      'helpers',
      'module',
      'exports',
      'Mustache',
      'Handlebars',
      'require',
      'registeredHelpers',
      `${code}\nreturn { moduleExports: module.exports || helpers, registeredHelpers };`,
    );
    const evaluated = evaluator(helpers, module, exports, Mustache, Handlebars, require, registeredHelpers) as {
      moduleExports?: unknown;
      registeredHelpers?: Record<string, unknown>;
    };

    return {
      ...(evaluated?.moduleExports && typeof evaluated.moduleExports === 'object' ? evaluated.moduleExports as Record<string, unknown> : {}),
      ...(evaluated?.registeredHelpers || registeredHelpers),
    };
  }

  private convertRegisteredHelperToMustache(_name: string, helper: unknown): unknown {
    if (typeof helper !== 'function') {
      return helper;
    }

    return () => (text: string, render: (value: string) => string) => {
      const renderedText = render(text).trim();

      try {
        return helper(renderedText);
      } catch (error) {
        console.error('Failed to execute client-select mustache helper', error);
        return renderedText;
      }
    };
  }

  private decorateTemplateValue(value: any): any {
    if (Array.isArray(value)) {
      return value.map((entry, index) => {
        const decoratedEntry = this.decorateTemplateValue(entry);
        if (decoratedEntry && typeof decoratedEntry === 'object' && !Array.isArray(decoratedEntry)) {
          return {
            ...decoratedEntry,
            '@last': index === value.length - 1,
          };
        }

        return {
          value: decoratedEntry,
          '@last': index === value.length - 1,
        };
      });
    }

    if (value && typeof value === 'object') {
      return Object.entries(value).reduce<Record<string, unknown>>((accumulator, [key, entryValue]) => {
        accumulator[key] = this.decorateTemplateValue(entryValue);
        return accumulator;
      }, {});
    }

    return value;
  }

  private formatPlainColumnValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }

  /**
   * Retrieves the reference location from the model based on the field configuration.
   * @returns The reference location or null if not available or invalid.
   */
  private getReferenceLocation(): ClientSelectLocationValue | null {
    const fieldKey = this.fieldProps.locationReferenceFieldKey;
    if (!fieldKey) {
      return null;
    }

    const locationValue = this.getNestedValue(this.model, fieldKey) as ClientSelectLocationValue | null;
    if (!locationValue) {
      return null;
    }

    const latitude = Number(locationValue.latitude);
    const longitude = Number(locationValue.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return null;
    }

    return { latitude, longitude };
  }

  /**
   * Retrieves a nested value from the given source object based on the provided path.
   * @param source The source object to retrieve the value from.
   * @param path The path to the nested value, using dot notation.
   * @returns The nested value or undefined if not found.
   */
  private getNestedValue(source: any, path: string): any {
    if (!source || !path) {
      return undefined;
    }

    return path.split('.').reduce((currentValue: any, segment: string) => currentValue?.[segment], source);
  }

  private setNestedValue(target: Record<string, unknown>, path: string, value: unknown): void {
    const segments = path.split('.');
    let currentTarget: Record<string, unknown> = target;

    segments.forEach((segment, index) => {
      if (index === segments.length - 1) {
        currentTarget[segment] = value;
        return;
      }

      const existingValue = currentTarget[segment];
      if (!existingValue || typeof existingValue !== 'object' || Array.isArray(existingValue)) {
        currentTarget[segment] = {};
      }
      currentTarget = currentTarget[segment] as Record<string, unknown>;
    });
  }

  /**
   * Formats a distance value into a human-readable string. If the distance is less than 1000 meters, it is displayed in meters. Otherwise, it is displayed in kilometers with one decimal place for distances less than 10 km and no decimal places for distances 10 km or greater.
   * @param distance The distance value to format.
   * @returns A formatted distance string.
   */
  private formatDistance(distance: unknown): string {
    if (typeof distance !== 'number' || !Number.isFinite(distance)) {
      return '—';
    }

    if (distance < 1000) {
      return `${Math.round(distance)} m`;
    }

    const kilometers = distance / 1000;
    return `${kilometers.toFixed(kilometers < 10 ? 1 : 0)} km`;
  }

  /**
   * Binds the form control to the component when it becomes available. This method checks if the form control is already bound to prevent multiple subscriptions. It retrieves the resolved form control from the field or the component's input and subscribes to its value changes to synchronize the selected records when the form control value changes. It also performs an initial synchronization of the selected records from the form control's current value. This method should be called whenever there is a possibility that the form control has become available, such as after context initialization or when the field configuration changes.
   * @returns void
   */
  private bindFormControlWhenReady(): void {
    if (this.hasBoundFormControl) {
      return;
    }

    const control = this.getResolvedFormControl();
    if (!control) {
      return;
    }

    this.hasBoundFormControl = true;

    this.subsink.sink = control.valueChanges.subscribe((value) => {
      if (this.suppressValueSync) {
        return;
      }
      void this.syncSelectedRecordsFromValue(value);
    });

    void this.syncSelectedRecordsFromValue(control.value);
  }

  /**
   * Retrieves the resolved form control from the component's input or the field configuration. It first checks if the form control is provided directly to the component via an input. If not, it attempts to retrieve it from the field's form control. If neither is available, it returns null. This method centralizes the logic for determining which form control to use for synchronizing the selected records, ensuring that the component can work correctly whether the form control is passed directly or accessed through the field configuration.
   * @returns The resolved form control or null if none is available.
   */
  private getResolvedFormControl(): AbstractControl | null {
    return this.formControl ?? this.field?.formControl ?? null;
  }

  /**
   * Retrieves the current stored value from the form control or the model. This method first attempts to get the value from the resolved form control. If the form control is not available, it then tries to retrieve the value from the model using the appropriate field key. The field key is determined by checking both the field's key and its props for a 'key' property. If a valid field key and target model are found, it uses the miscService to get the deep child value from the model based on that key. This method provides a unified way to access the current value that should be used for synchronizing the selected records, regardless of whether it comes from the form control or directly from the model.
   * @returns The current stored value or null if not available.
   */ 
  private getCurrentStoredValue(): any {
    const control = this.getResolvedFormControl();
    if (control) {
      return control.value;
    }

    const fieldKey = typeof this.field?.key === 'string'
      ? this.field.key
      : typeof this.field?.props?.['key'] === 'string'
        ? this.field.props['key']
        : null;

    const targetModel = this.model ?? this.field?.model;
    if (!fieldKey || !targetModel || typeof targetModel !== 'object') {
      return null;
    }

    return this.miscService.getDeepChildValue(targetModel, fieldKey);
  }

  /**
   * Writes the selected value to the model. This method determines the appropriate field key and target model, then uses the miscService to set the value deeply within the model. The field key is determined by checking the field's key and props for a 'key' property. The target model is determined by checking the component's model and the field's model. If both the field key and target model are available, it sets the value at the specified path within the target model. This method ensures that the selected value from the client select component is properly reflected in the form's model, allowing for correct form submission and data handling.
   * @param value The value to be written to the model.
   * @returns void
   */
  private writeSelectionToModel(value: any): void {
    const fieldKey = typeof this.field?.key === 'string'
      ? this.field.key
      : typeof this.field?.props?.['key'] === 'string'
        ? this.field.props['key']
        : null;

    if (!fieldKey) {
      return;
    }

    const targetModel = this.model ?? this.field?.model;
    if (!targetModel || typeof targetModel !== 'object') {
      return;
    }

    this.miscService.setDeepChildValue(targetModel, fieldKey, value);
  }

}